import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import apiClient from "../services/api";

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const adminUser = localStorage.getItem("adminUser");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }

    if (adminUser) {
      setUser(JSON.parse(adminUser));
    }

    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            {user && <p className="text-sm text-gray-600">Welcome, {user.name}</p>}
          </div>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate("/")}>
              View Site
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="blog">Blog Posts</TabsTrigger>
            <TabsTrigger value="donations">Donations</TabsTrigger>
            <TabsTrigger value="users">Admin Users</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle>Intake Enquiries</CardTitle>
                  <CardDescription>Help a Horse submissions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.intake_enquiries?.total || 0}</div>
                  <p className="text-sm text-gray-600 mt-2">
                    {stats?.intake_enquiries?.new || 0} new
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Contact Messages</CardTitle>
                  <CardDescription>General inquiries</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.contact_messages?.total || 0}</div>
                  <p className="text-sm text-gray-600 mt-2">
                    {stats?.contact_messages?.new || 0} new
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Volunteer Applications</CardTitle>
                  <CardDescription>People wanting to help</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.volunteer_applications?.total || 0}</div>
                  <p className="text-sm text-gray-600 mt-2">
                    {stats?.volunteer_applications?.new || 0} new
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Rehoming Applications</CardTitle>
                  <CardDescription>Adoption requests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.rehoming_applications?.total || 0}</div>
                  <p className="text-sm text-gray-600 mt-2">
                    {stats?.rehoming_applications?.new || 0} new
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Welfare Reports</CardTitle>
                  <CardDescription>Concern reports</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.welfare_reports?.total || 0}</div>
                  <p className="text-sm text-gray-600 mt-2">
                    {stats?.welfare_reports?.new || 0} new
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Total Donations</CardTitle>
                  <CardDescription>Fundraising progress</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    £{stats?.donations?.total_amount?.toFixed(2) || "0.00"}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {stats?.donations?.total_donations || 0} donations
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>News Posts</CardTitle>
                <CardDescription>Blog content management</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-8">
                  <div>
                    <div className="text-2xl font-bold">{stats?.news_posts?.total || 0}</div>
                    <p className="text-sm text-gray-600">Total posts</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats?.news_posts?.published || 0}</div>
                    <p className="text-sm text-gray-600">Published</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="enquiries">
            <Card>
              <CardHeader>
                <CardTitle>Form Submissions</CardTitle>
                <CardDescription>View and manage all form submissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button onClick={() => navigate("/admin/intake-enquiries")}>
                    View Intake Enquiries ({stats?.intake_enquiries?.total || 0})
                  </Button>
                  <Button onClick={() => navigate("/admin/contact-messages")} className="ml-4">
                    View Contact Messages ({stats?.contact_messages?.total || 0})
                  </Button>
                  <Button onClick={() => navigate("/admin/volunteer-applications")} className="ml-4">
                    View Volunteer Applications ({stats?.volunteer_applications?.total || 0})
                  </Button>
                  <Button onClick={() => navigate("/admin/rehoming-applications")} className="ml-4">
                    View Rehoming Applications ({stats?.rehoming_applications?.total || 0})
                  </Button>
                  <Button onClick={() => navigate("/admin/welfare-reports")} className="ml-4">
                    View Welfare Reports ({stats?.welfare_reports?.total || 0})
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blog">
            <Card>
              <CardHeader>
                <CardTitle>Blog Management</CardTitle>
                <CardDescription>Create and manage news posts</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/admin/blog")}>
                  Manage Blog Posts
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="donations">
            <Card>
              <CardHeader>
                <CardTitle>Donation Management</CardTitle>
                <CardDescription>View donation history and statistics</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/admin/donations")}>
                  View All Donations
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Admin User Management</CardTitle>
                <CardDescription>Manage admin accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/admin/users")}>
                  Manage Admin Users
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
